import * as FileSaver from 'file-saver'
import { useCallback, useEffect, useState } from 'react'

import { startInteraction } from '@app/domain/program/program.rest'
import type { ISortDirection } from '@app/lib/sorting'
import { useAuthenticatedRequestor } from '@app/rest/rest.hooks'
import { retrieveVisitDefinition, updateStatus as updateStatusRequest } from '@app/state/intake/rest'
import type { IApiVisitDefinition } from '@app/state/visits/helpers'
import * as Helpers from '@app/state/visits/helpers'
import {
  getDocumentFileName,
  retrieveDefinitionList,
} from '@app/state/visits/rest'

import {
  getDerivedValues,
  getLocations,
  getVisit,
  getVisitPrograms,
  getVisits,
  requestVisitPDF,
  submitResponses,
} from './visit.rest'

import type {
  IUseGetVisitDefinition,
  IUseLocations,
  IVisit,
  IUseListVisits,
  IVisitLocation,
  IUseGetVisitPrograms,
  IUseEditEntryReponses,
  IUseDerivedValuesForVisit,
  IUseStartInteraction,
  IUseGetVisitPdf,
  IUseVisit,
  VisitStatusId,
} from './visit.interface'
import type { IProgram } from '../program/program.interface'


export function useVisit(visitId: string): IUseVisit {
  const requestor = useAuthenticatedRequestor()

  const [visit, setVisit] = useState<IVisit>()

  const fetchVisit = useCallback(async () => {
    const response = await requestor(getVisit(visitId))

    if (response.status === 200) {
      const v = response.data.visit

      setVisit(Helpers.convertApiVisit(v.visit, v.patient))
    } else {
      throw new Error(`server returned a non 200 response '${response.status}'`)
    }
  }, [visitId, requestor])

  useEffect(() => {
    if (visitId) {
      fetchVisit()
    }
  }, [fetchVisit, visitId])

  return {
    visit,
    getVisit: fetchVisit,
  }
}

/**
 * A hook for simply updating the status of a visit.
 *
 * This is best used to be able to update a visit's status and reliably wait for the request to
 * complete. Existing implementations are a mix of redux state, sagas, and custom hooks which do not
 * share state and are excessively complex. This exists on its own because no other hooks make sense
 * to add this to without duplicating, overcomplicating, or adding even more complexity to the code.
 *
 * @returns A function to update the status of a visit.
 */
export function useUpdateVisitStatus() {
  const requestor = useAuthenticatedRequestor()

  const updateStatus = useCallback(async (visitId: string, status: VisitStatusId) => {
    // FIXME: Note that this is referencing an HTTP request from the `intake` module where it
    // doesn't seem to belong.  Consider consolidating actions to relevant modules.
    const response = await requestor(updateStatusRequest(visitId, status))
    if (response.status === 200) {
      return Promise.resolve()
    } else {
      console.error(`Server returned a non 200 response '${response.status}' when updating visit status for visit ${visitId}, swallowing`)
      return Promise.reject()
    }
  }, [requestor])

  return {
  /**
   * Updates the status of a visit, does not return anything.
   * Resolves if the request is successful, rejects if the request is not successful, swallowing
   * any errors but logging them.
   */
    updateStatus,
  }
}

export function useListVisits(): IUseListVisits {
  const [visits, setVisits] = useState<IVisit[]>([])
  const [pageCount, setPageCount] = useState<number>(0)
  const [totalNumberOfRecords, setTotalNumberOfRecords] = useState<number>(0)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const requestor = useAuthenticatedRequestor()

  const fetchVisits = useCallback(
    async (
      pageNumber: number,
      recordsPerPage: number,
      location?: string,
      mrn?: string,
      patientName?: string,
      status?: string[],
      visitDate?: string,
      patientId?: string,
      sortDirection?: ISortDirection,
      sortField?: string,
      type?: string[],
    ) => {
      setIsLoading(true)
      const response = await requestor(
        getVisits(
          pageNumber,
          recordsPerPage,
          location,
          mrn,
          patientName,
          status,
          visitDate,
          patientId,
          sortDirection,
          sortField,
          type,
        ),
      )

      if (response.status === 200) {
        const responseVisits = response.data.visits.map((v) => {
          return Helpers.convertApiVisit(v.visit, v.patient)
        })
        setVisits(responseVisits)

        setPageCount(response.data.pageCount)
        setTotalNumberOfRecords(response.data.totalNumberOfRecords)
      } else {
        throw new Error(
          `server returned returned a non 200 response '${response.status}'`,
        )
      }

      setIsLoading(false)
    },
    [requestor],
  )


  return {
    visits,
    isLoading,
    pageCount: pageCount,
    totalNumberOfRecords: totalNumberOfRecords,
    getVisits: fetchVisits,
  }
}

export function useLocations(): IUseLocations {
  const [locations, setLocations] = useState<IVisitLocation[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const requestor = useAuthenticatedRequestor()

  const fetchLocations = useCallback(async () => {
    setIsLoading(true)
    const response = await requestor(getLocations())

    if (response.status === 200) {
      setLocations(Array.from(Object.values(response.data.locations)))
    } else {
      throw new Error(
        `server returned returned a non 200 response '${response.status}'`,
      )
    }

    setIsLoading(false)
  }, [requestor])

  useEffect(() => {
    fetchLocations()
  }, [fetchLocations])

  return {
    locations,
    getLocations: fetchLocations,
    isLoading,
  }
}

interface IUseVisitDefinitions {
  definitions: IApiVisitDefinition[]
  getDefinitions: () => void
}

export function useVisitDefinitions(location?: string): IUseVisitDefinitions {
  const requestor = useAuthenticatedRequestor()
  const [definitions, setDefinitions] = useState<IApiVisitDefinition[]>([])

  const fetchVisitDefinitions = useCallback(async () => {
    const response = await requestor(retrieveDefinitionList(location))
    if (response.status === 200) {
      setDefinitions(response.data.definitions)
    } else {
      throw new Error(
        `server returned returned a non 200 response '${response.status}'`,
      )
    }
  }, [location, requestor])

  useEffect(() => {
    fetchVisitDefinitions()
  }, [fetchVisitDefinitions])

  return {
    definitions,
    getDefinitions: fetchVisitDefinitions,
  }
}

export function useGetVisitDefinition(visitId: string): IUseGetVisitDefinition {
  const [visitDefinition, setVisitDefinition] = useState<IApiVisitDefinition>()
  const requestor = useAuthenticatedRequestor()

  const fetchVisitDefinition = useCallback(async () => {
    if (visitId) {
      const response = await requestor(retrieveVisitDefinition(visitId))

      if (response.status === 200) {
        setVisitDefinition(response.data.definition)
      } else {
        throw new Error(
          `server returned returned a non 200 response '${response.status}'`,
        )
      }
    } else {
      setVisitDefinition(null)
    }
  }, [requestor, visitId])

  useEffect(() => {
    fetchVisitDefinition()
  }, [fetchVisitDefinition])

  return {
    visitDefinition,
  }
}

export function useGetVisitPrograms(visitId: string): IUseGetVisitPrograms {
  const [programs, setPrograms] = useState<IProgram[]>([])
  const requestor = useAuthenticatedRequestor()

  const fetchVisitPrograms = useCallback(async () => {
    if (visitId) {
      const response = await requestor(getVisitPrograms(visitId))

      if (response.status === 200) {
        setPrograms(response.data.programs)
      } else {
        throw new Error(
          `server returned returned a non 200 response '${response.status}'`,
        )
      }
    } else {
      setPrograms([])
    }
  }, [requestor, visitId])

  return {
    getVisitPrograms: fetchVisitPrograms,
    programs,
  }
}

export function useGetVisitPdf(): IUseGetVisitPdf {
  const requestor = useAuthenticatedRequestor()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchVisitPdf = useCallback(
    async (visitId: string, templateId: string) => {
      setError(null)
      setLoading(true)
      try {
        const response = await requestor(requestVisitPDF(visitId, templateId))

        if (response.status === 200) {
          const fileName = getDocumentFileName(
            response.headers['content-disposition'],
            'patient-entry',
            'pdf',
          )
          FileSaver.saveAs(response.data, fileName)
        } else {
          throw new Error(
            `server returned returned a non 200 response '${response.status}'`,
          )
        }
      } catch (e) {
        setError('Failed to download visit report.')
      } finally {
        setLoading(false)
      }
    },
    [requestor],
  )

  const clearError = useCallback(() => setError(null), [])

  return {
    getVisitPdf: fetchVisitPdf,
    visitPdfError: error,
    clearVisitPdfError: clearError,
    isVisitPdfLoading: loading,
  }
}

export function useEditEntryResponses(): IUseEditEntryReponses {
  const requestor = useAuthenticatedRequestor()
  const editEntryResponses = useCallback(
    async (entryId: string, responses: Record<string, string>) => {
      const response = await requestor(submitResponses(entryId, responses))
      if (response.status === 200) {
        return response.data
      } else {
        throw new Error(
          `Could not edit instrument responses. '${response.status}'`,
        )
      }
    },
    [requestor],
  )

  return {
    editEntryResponses,
  }
}

export function useDerivedValuesForVisit(
  visitId: string,
): IUseDerivedValuesForVisit {
  const [derivedValues, setDerivedValues] = useState<Record<string, unknown>>(
    {},
  )
  const requestor = useAuthenticatedRequestor()

  const fetchDerivedValues = useCallback(async () => {
    if (visitId) {
      const response = await requestor(getDerivedValues(visitId))

      if (response.status === 200) {
        setDerivedValues(response.data.derivedValues)
      } else {
        throw new Error(
          `server returned returned a non 200 response '${response.status}'`,
        )
      }
    } else {
      setDerivedValues({})
    }
  }, [requestor, visitId])

  useEffect(() => {
    fetchDerivedValues()
  }, [fetchDerivedValues])

  return {
    derivedValues,
    getDerivedValues: fetchDerivedValues,
  }
}

export function useStartInteraction(): IUseStartInteraction {
  const requestor = useAuthenticatedRequestor()
  const setInteraction = useCallback(
    async (visitId: string, selectedInteractionIds: string[]) => {
      const response = await requestor(
        startInteraction(visitId, selectedInteractionIds),
      )
      if (response.status === 200) {
        return response.data
      } else {
        throw new Error(`Could not start interaction. '${response.status}'`)
      }
    },
    [requestor],
  )

  return {
    setInteraction,
  }
}
